import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RegionSelect from '@/components/common/RegionSelect';
import { useCurrencyRegions } from '@/hooks/useCurrencyRegions';
import { regionBySlug } from '@/lib/regions';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage, margin, PriceEntry, ScopedPrices, today } from '@/lib/pricing';

export type PriceScope = { tier: string } | { plan: string };

export type PriceTarget = {
  configId: string;
  configName: string;
  scope: PriceScope;
  scopeKey: string;
  scopeLabel: string;
  prices: ScopedPrices;
  cost: number | null;
};

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export default function SetPriceDialog({
  target,
  defaultRegion,
  onClose,
}: {
  target: PriceTarget | null;
  defaultRegion: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = React.useState('');

  const regions = useCurrencyRegions();
  const [region, setRegion] = React.useState(defaultRegion);
  // What the default region's price converts to today. Offered, never
  // applied: the rate moves, and a stored price should not.
  const suggestion = useQuery({
    queryKey: ['price-suggestion', target?.configId, target?.scopeKey, region],
    enabled: Boolean(target) && Boolean(region),
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const scope = target!.scope as { tier?: string; plan?: string };
      const res = await api.get('/global-admin/price-suggestion/', {
        params: {
          region,
          server_config: target!.configId,
          tier: scope.tier ?? '',
          plan: scope.plan ?? '',
        },
      });
      return res.data as {
        amount?: string;
        currency?: string;
        rate?: string;
        rate_date?: string;
        from_region?: string;
        from_amount?: string;
        from_currency?: string;
      };
    },
  });

  const [date, setDate] = React.useState(today());
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!target) return;
    setAmount('');
    setRegion(defaultRegion);
    setDate(today());
    setNotes('');
  }, [target, defaultRegion]);

  const priced = () => {
    queryClient.invalidateQueries({ queryKey: ['hetzner-configs'] });
    queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
  };

  const removePrice = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/hetzner-prices/${id}/`);
    },
    onSuccess: () => { priced(); toast.success('Price removed.'); },
    onError: (err: any) => toast.error(apiMessage(err)),
  });

  const history: PriceEntry[] = React.useMemo(() => {
    if (!target) return [];
    const byRegion = target.prices[target.scopeKey] ?? {};
    return Object.values(byRegion).flat()
      .sort((a, b) => b.effective_date.localeCompare(a.effective_date));
  }, [target]);

  const collision = history.find((row) => row.effective_date === date && row.region === region);

  const setPrice = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      if (collision) await api.delete(`/global-admin/hetzner-prices/${collision.id}/`);
      return api.post('/global-admin/hetzner-prices/', {
        server_config: target!.configId,
        ...target!.scope,
        region,
        monthly_price: amount,
        effective_date: date,
        notes,
        set_by: 'admin',
      });
    },
    onSuccess: () => {
      priced();
      toast.success(collision ? 'Price replaced.' : 'Price set.');
      onClose();
    },
    onError: (err: any) => toast.error(apiMessage(err)),
  });

  const chosen = regionBySlug(regions.data?.regions ?? [], region);
  const shown = margin(
    amount && chosen ? { currency: chosen.currency, amount } : null,
    target?.cost ?? null,
  );

  return (
    <Dialog open={target != null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="set-price-dialog">
        <DialogHeader>
          <DialogTitle>{target?.configName} · {target?.scopeLabel}</DialogTitle>
          <DialogDescription>
            A server keeps the price in force on the day it started, so a change
            here only ever applies to servers created from that date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="set-price-amount">Monthly price</Label>
              <Input id="set-price-amount" type="number" min="0" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)} data-testid="set-price-amount" />
              {suggestion.data?.amount && !amount && (
                <button
                  type="button"
                  className="text-left text-xs text-muted-foreground underline"
                  onClick={() => setAmount(suggestion.data!.amount!)}
                  data-testid="set-price-suggestion"
                >
                  Use {Number(suggestion.data.amount).toLocaleString()} {suggestion.data.currency}
                  {' '}— {suggestion.data.from_amount} {suggestion.data.from_currency} in{' '}
                  {suggestion.data.from_region} at {suggestion.data.rate} ({suggestion.data.rate_date})
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Region</Label>
              <RegionSelect value={region} onChange={setRegion} testId="set-price-region" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-price-date">In force from</Label>
              <Input id="set-price-date" type="date" value={date}
                onChange={(e) => setDate(e.target.value)} data-testid="set-price-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-price-note">Note</Label>
              <Input id="set-price-note" value={notes}
                onChange={(e) => setNotes(e.target.value)} data-testid="set-price-note" />
            </div>
          </div>

          <p className="text-sm" data-testid="set-price-margin">
            {target?.cost == null ? (
              <span className="text-muted-foreground">Our cost is unknown, so the margin cannot be worked out.</span>
            ) : shown ? (
              <>
                <span className="text-muted-foreground">Costs us {target.cost.toFixed(2)} EUR. Margin </span>
                <span className={shown.amount < 0 ? 'font-medium text-destructive' : 'font-medium text-emerald-600'}>
                  {shown.amount.toFixed(2)} EUR ({shown.percent.toFixed(0)}%)
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">
                Costs us {target.cost.toFixed(2)} EUR.{chosen && chosen.currency !== 'EUR'
                  ? ` Hetzner bills in EUR, so no margin is shown for a region billed in ${chosen.currency}.`
                  : ''}
              </span>
            )}
          </p>

          {history.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium">Every price set for this cell</p>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1">From</th>
                    <th className="py-1">Region</th>
                    <th className="py-1">Price</th>
                    <th className="py-1">Set by</th>
                    <th className="py-1">Note</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-t ${row.region === region ? 'bg-muted/40' : ''}`}
                      data-testid={`set-price-row-${row.id}`}
                    >
                      <td className="py-2">{day(row.effective_date)}</td>
                      <td
                        className="py-2 text-muted-foreground"
                        data-testid={`set-price-row-region-${row.id}`}
                      >
                        {regionBySlug(regions.data?.regions ?? [], row.region)?.name ?? row.region}
                      </td>
                      <td className="py-2 font-medium">{Number(row.monthly_price).toLocaleString()} {row.currency}</td>
                      <td className="py-2 text-muted-foreground">{row.set_by}</td>
                      <td className="py-2 text-muted-foreground">{row.notes}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="ghost" title="Remove this price"
                          onClick={() => removePrice.mutate(row.id)}
                          data-testid={`set-price-delete-${row.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => setPrice.mutate()}
            disabled={setPrice.isPending || !amount || !region || !date}
            data-testid="set-price-save"
          >
            {setPrice.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {collision ? 'Replace' : 'Set price'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
